import { COUNT_PER_PAGE, YN } from '../../constants/Constants';
import React, { useEffect, useMemo, useState } from 'react';
import { columnDateTime, columnUseYn } from '../../utils/dataTableUtil';

import { CodeUtil } from '../../utils/CodeUtil';
import { Column } from 'primereact/column';
import { CommonUtil } from '../../utils/commonUtil';
import CustomerCompanyDialog from '../../components/Customer/CustomerCompanyDialog';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { MyInfoUtil } from '../../utils/myInfoUtil';
import { Panel } from 'primereact/panel';
import { ProgressBar } from 'primereact/progressbar';
import { RadioButton } from 'primereact/radiobutton';
import SearchMenu from '../../components/Menu/SearchMenu';
import ServiceMenu from '../../components/Menu/ServiceMenu';
import { ServiceProvider } from '../../services';
import _ from 'lodash';
import { myInfoSelector } from '../../recoil/selectors';
import { useAuthorizedFetch } from '../../hooks/useAuthorizedFetch';
import { useRecoilValueLoadable } from 'recoil';

const DEFAULT_SEARCH_CONDITIONS = {
  associations: [],
  enterprises: [],
  shops: [],
  searchText: '',
  useYN: 'Y',
};
const DEFAULT_LAZY_PARAMS = {
  first: 0,
  rows: 10,
  page: 0,
};
const customerCompanyService = ServiceProvider.customerCompany;

const CustomerCompanyList = () => {
  const myInfoLoadable = useRecoilValueLoadable(myInfoSelector);
  const { myAssociationId, myEnterpriseId } = useMemo(
    () => MyInfoUtil.unpack(myInfoLoadable.contents),
    [myInfoLoadable.contents]
  );

  const [searchConditions, setSearchConditions] = useState({
    ...DEFAULT_SEARCH_CONDITIONS,
  });
  const [lazyParams, setLazyParams] = useState({ ...DEFAULT_LAZY_PARAMS });

  const { loading, totalRecords, data, getList, initList } = useAuthorizedFetch(
    customerCompanyService,
    searchConditions,
    setSearchConditions,
    DEFAULT_SEARCH_CONDITIONS,
    lazyParams.page,
    lazyParams.rows
  );
  const [currentCustomerCompanyId, setCurrentCustomerCompanyId] =
    useState(null);
  const [codes, setCodes] = useState({});

  useEffect(() => {
    (async () => {
      if (myInfoLoadable.state === 'hasValue') {
        const myInfo = MyInfoUtil.unpack(myInfoLoadable.contents);

        const [associations, associationId] =
          await CodeUtil.getAssociationCodes(myInfo, {
            preSelectedAssociationId: myAssociationId,
          });
        const [enterprises, enterpriseId] = await CodeUtil.getEnterpriseCodes(
          myInfo,
          {
            isParentEmpty: _.isEmpty(associations),
            associationId: myAssociationId,
          }
        );
        const [shops, shopId] = await CodeUtil.getShopCodes(myInfo, {
          isParentEmpty: _.isEmpty(enterprises),
          enterpriseId: myEnterpriseId,
        });

        setCodes((ps) => ({ ...ps, associations, enterprises, shops }));

        await getList({
          ...searchConditions,
          associations: [associationId],
          enterprises: [enterpriseId],
          shops: [shopId],
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myInfoLoadable.state]);

  const searchInList = () => {
    getList(
      searchConditions,
      _.get(DEFAULT_LAZY_PARAMS, 'page'),
      _.get(DEFAULT_LAZY_PARAMS, 'rows')
    );
    setLazyParams(DEFAULT_LAZY_PARAMS);
  };

  return (
    <div className="grid">
      <div className="col-12">
        {(() => {
          switch (myInfoLoadable.state) {
            case 'hasValue': {
              const myInfo = MyInfoUtil.unpack(myInfoLoadable.contents);
              return (
                <>
                  <SearchMenu
                    onInit={() => initList()}
                    onSearch={() => searchInList()}
                  >
                    <div className="field col-12 sm:col-6 lg:col-3">
                      <label htmlFor="range">소속협회</label>
                      <MultiSelect
                        className="w-full"
                        options={codes.associations}
                        display="chip"
                        placeholder="전체 협회"
                        value={searchConditions.associations}
                        filter
                        showSelectAll={false}
                        onChange={async ({ value }) => {
                          const [enterprises] =
                            await CodeUtil.getEnterpriseCodes(myInfo, {
                              isParentEmpty: _.isEmpty(
                                _.get(codes, 'associations')
                              ),
                              associationId: value,
                            });
                          const [shops] = await CodeUtil.getShopCodes(myInfo, {
                            isParentEmpty: _.isEmpty(enterprises),
                          });

                          CommonUtil.Func.handleSelectAll(
                            value,
                            'associations',
                            {
                              enterprises,
                              shops,
                            },
                            {
                              enterprises: [],
                              shops: [],
                            },
                            codes,
                            setCodes,
                            setSearchConditions
                          );
                        }}
                      />
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-3">
                      <label htmlFor="range">소속점검법인</label>
                      <MultiSelect
                        className="w-full"
                        options={codes.enterprises}
                        display="chip"
                        placeholder="전체 업체"
                        value={searchConditions.enterprises}
                        filter
                        showSelectAll={false}
                        onChange={async ({ value }) => {
                          const [shops] = await CodeUtil.getShopCodes(myInfo, {
                            isParentEmpty: _.isEmpty(
                              _.get(codes, 'enterprises')
                            ),
                            enterpriseId: value,
                          });

                          CommonUtil.Func.handleSelectAll(
                            value,
                            'enterprises',
                            {
                              shops,
                            },
                            {
                              shops: [],
                            },
                            codes,
                            setCodes,
                            setSearchConditions
                          );
                        }}
                      />
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-3">
                      <label htmlFor="range">소속점검장</label>
                      <MultiSelect
                        className="w-full"
                        options={codes.shops}
                        display="chip"
                        placeholder="전체 점검장"
                        value={searchConditions.shops}
                        filter
                        showSelectAll={false}
                        onChange={({ value }) => {
                          CommonUtil.Func.handleSelectAll(
                            value,
                            'shops',
                            codes.shops,
                            null,
                            codes,
                            setCodes,
                            setSearchConditions
                          );
                        }}
                      />
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-3">
                      <label htmlFor="range">사용여부</label>
                      <div className="flex flex-auto flex-wrap align-items-center justify-content-start mt-2 gap-3">
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_useYN_all"
                            name="useYN"
                            value=""
                            onChange={({ value }) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                useYN: value,
                              }));
                            }}
                            checked={searchConditions.useYN === ''}
                          />
                          <label htmlFor="radio_useYN_all">전체</label>
                        </div>
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_useYN_y"
                            name="useYN"
                            value="Y"
                            onChange={({ value }) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                useYN: value,
                              }));
                            }}
                            checked={searchConditions.useYN === YN.YES}
                          />
                          <label htmlFor="radio_useYN_y">사용</label>
                        </div>
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_useYN_n"
                            name="useYN"
                            value="N"
                            onChange={({ value }) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                useYN: value,
                              }));
                            }}
                            checked={searchConditions.useYN === YN.NO}
                          />
                          <label htmlFor="radio_useYN_n">미사용</label>
                        </div>
                      </div>
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-3">
                      <label htmlFor="range">키워드</label>
                      <InputText
                        className="w-full"
                        value={searchConditions.searchText}
                        placeholder="매매상사 이름, 사업자등록번호로 검색"
                        onChange={({ target: { value } }) => {
                          let v = value;
                          const numRegExp = /[0-9]/g;
                          if (
                            numRegExp.test(Number(value[0])) &&
                            value[0] === '0'
                          ) {
                            v = CommonUtil.Formatter.phone(value);
                          }
                          setSearchConditions((ps) => ({
                            ...ps,
                            searchText: v,
                          }));
                        }}
                        onKeyDown={(e) =>
                          e.key === 'Enter' ? searchInList() : null
                        }
                      />
                    </div>
                  </SearchMenu>
                  <ServiceMenu
                    title={'매매상사'}
                    onClickRegisterBtn={() => setCurrentCustomerCompanyId(0)}
                    excelData={{
                      type: 'customerCompany',
                      params: {
                        ...searchConditions,
                        page: lazyParams.page + 1,
                        size: lazyParams.rows,
                      },
                    }}
                    buttonVisible={CommonUtil.Func.checkUserAuth(
                      myInfoLoadable.contents
                    )}
                  />

                  <Panel className="shadow-1 mb-3">
                    <DataTable
                      loading={loading}
                      value={data}
                      lazy
                      rows={lazyParams.rows}
                      first={lazyParams.first}
                      totalRecords={totalRecords}
                      onPage={async (event) => {
                        setLazyParams(event);
                        console.log(event);
                        await getList(searchConditions, event.page, event.rows);
                      }}
                      paginator
                      responsiveLayout="scroll"
                      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                      currentPageReportTemplate="전체 {totalRecords}건 중 {first} ~ {last}"
                      rowsPerPageOptions={COUNT_PER_PAGE}
                      resizableColumns
                      showGridlines
                      emptyMessage="데이터가 없습니다."
                    >
                      <Column
                        header="순번"
                        body={(rowData, column) => (
                          <span>{column.rowIndex + 1}</span>
                        )}
                      />
                      <Column
                        field="companyName"
                        header="매매상사명"
                        body={({ customerCompanyId, companyName }) => (
                          <span
                            className="text-primary cursor-pointer"
                            onClick={() =>
                              setCurrentCustomerCompanyId(customerCompanyId)
                            }
                          >
                            {companyName}
                          </span>
                        )}
                      />
                      <Column
                        field="businessRegistrationName"
                        header={
                          <div>
                            <div>사업자명</div>
                            <div>사업자등록번호</div>
                          </div>
                        }
                        body={({
                          businessRegistrationName,
                          businessRegistrationNumber,
                        }) => (
                          <div>
                            <span>{businessRegistrationName}</span>
                            <br />
                            <span>
                              {businessRegistrationNumber &&
                                CommonUtil.Formatter.biz(
                                  businessRegistrationNumber
                                )}
                            </span>
                          </div>
                        )}
                      />
                      <Column
                        field="businessRegistrationName"
                        header={
                          <div>
                            <div>대표자명</div>
                            <div>대표전화</div>
                            <div>팩스</div>
                          </div>
                        }
                        body={({ representative, mainPhone, fax }) => (
                          <div>
                            <span>{representative}</span>
                            <br />
                            <span>
                              {mainPhone &&
                                CommonUtil.Formatter.phone(mainPhone)}
                            </span>
                            <br />
                            <span>{fax && CommonUtil.Formatter.fax(fax)}</span>
                          </div>
                        )}
                      />
                      <Column
                        field="unionName"
                        header={
                          <div>
                            <div>소속매매조합</div>
                            <div>소속매매단지</div>
                          </div>
                        }
                        body={({ unionName, complexName }) => (
                          <div>
                            <div>{unionName}</div>
                            <div>{complexName}</div>
                          </div>
                        )}
                      />
                      <Column
                        field="address"
                        header={
                          <div>
                            <div>우편번호</div>
                            <div>주소</div>
                          </div>
                        }
                        body={({
                          shopId,
                          postcode,
                          address,
                          addressDetail,
                        }) => (
                          <>
                            <div
                              id={`address-${shopId}`}
                              className="white-space-nowrap overflow-hidden text-overflow-ellipsis"
                            >
                              <div>[{postcode}]</div>
                              <div>
                                {address} {addressDetail}
                              </div>
                            </div>
                          </>
                        )}
                      />
                      <Column
                        field="shopCode"
                        header={
                          <span>
                            소속점검장
                            <br />
                            점검장코드
                          </span>
                        }
                        body={({ regShopName, regShopCode }) => (
                          <span>
                            {regShopName}
                            {regShopCode && (
                              <>
                                <br />[{regShopCode}]
                              </>
                            )}
                          </span>
                        )}
                      />
                      <Column
                        field="associationCode"
                        header={
                          <span>
                            소속협회
                            <br />
                            협회코드
                          </span>
                        }
                        body={({ regAssociationName, regAssociationCode }) => (
                          <span>
                            {regAssociationName}
                            {regAssociationCode && (
                              <>
                                <br />[{regAssociationCode}]
                              </>
                            )}
                          </span>
                        )}
                      />
                      <Column
                        field="enterpriseCode"
                        header={
                          <span>
                            소속점검업체
                            <br />
                            점검업체코드
                          </span>
                        }
                        body={({ regEnterpriseName, regEnterpriseCode }) => (
                          <span>
                            {regEnterpriseName}
                            {regEnterpriseCode && (
                              <>
                                <br />[{regEnterpriseCode}]
                              </>
                            )}
                          </span>
                        )}
                      />
                      <Column
                        field="useYn"
                        header="사용여부"
                        body={({ useYn }) => columnUseYn(useYn)}
                        bodyClassName="text-center"
                      />

                      <Column field="note" header="비고" />
                      <Column
                        field="regTime"
                        header={
                          <div>
                            <span>등록사용자</span>
                            <br />
                            <span>등록일시</span>
                          </div>
                        }
                        body={({ regTime, regByUserName }) => (
                          <div>
                            <span>{regByUserName}</span>
                            <br />
                            <span>{columnDateTime(regTime)}</span>
                          </div>
                        )}
                      />
                      <Column
                        field="modTime"
                        header={
                          <div>
                            <span>수정사용자</span>
                            <br />
                            <span>수정일시</span>
                          </div>
                        }
                        body={({ modTime, modByUserName }) => (
                          <div>
                            <span>{modByUserName}</span>
                            <br />
                            <span>{columnDateTime(modTime)}</span>
                          </div>
                        )}
                      />
                      <Column
                        field="delTime"
                        header={
                          <div>
                            <span>삭제사용자</span>
                            <br />
                            <span>삭제일시</span>
                          </div>
                        }
                        body={({ delTime, delByUserName }) => (
                          <div>
                            <span>{columnDateTime(delTime)}</span>
                            <br />
                            <span>{delByUserName}</span>
                          </div>
                        )}
                      />
                    </DataTable>
                  </Panel>

                  {currentCustomerCompanyId !== null && (
                    <CustomerCompanyDialog
                      visible
                      userInfo={myInfoLoadable.contents}
                      customerCompanyId={currentCustomerCompanyId}
                      onHide={async () => {
                        setCurrentCustomerCompanyId(null);
                        await getList(
                          searchConditions,
                          lazyParams.page,
                          lazyParams.rows
                        );
                      }}
                    />
                  )}
                </>
              );
            }

            case 'loading':
              return <ProgressBar mode="indeterminate" />;

            case 'hasError':
              return null;

            default:
          }
        })()}
      </div>
    </div>
  );
};

const comparisonFn = (prevProps, nextProps) => {
  return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(CustomerCompanyList, comparisonFn);
